import React from 'react';
import type { ModuleDefinition } from './moduleRegistry';
import type { ReportData, AggBankingProps, AggBankingTable } from '../data/types';
import { ScreenSection } from '../primitives/ScreenPrimitives';
import { coverFonts, overviewColors, ptToPx, space } from '../data/designTokens';
import { DASH, ScreenStatStrip } from '../primitives/GstChartPrimitives';

// ════════════════════════════════════════════════════════════════
//  Aggregated Banking — Section 12. Consolidated bank statement
//  summary with bounce, credits/debits, and payment method tables.
//  Uses 8px compact text for 12-month columns (same as GST tables).
// ════════════════════════════════════════════════════════════════

const SF = coverFonts.screen;

// Section subtitle — also rendered by BankingOverview when this module's
// body is embedded there, so the copy lives in one place.
export const aggBankingSubtitle = (accountCount: number): string =>
    `Consolidated bank statement summary across all accounts · Source: Banking Statements · ${accountCount} Accounts`;

const tbl = {
    headerBg: '#F7F8F9',
    headerSize: 6,
    headerWeight: 600,
    headerColor: '#6B7080',
    headerLetterSpacing: 0.4,
    headerPadY: 4,
    headerPadX: 1,
    headerBorder: 1,
    headerBorderColor: overviewColors.border,
    bodySize: 5.5,
    // 4pt mono (~5.3px). 12 crore-value columns in the full Geist Mono TTF
    // overflowed their cells at 4.5pt and overlapped neighbours; 4pt + the
    // tighter padX/particular-flex below makes the widest value (₹15-char
    // "12,21,35,647.03") fit with a gap. Verified against the Lambda.
    bodyMonoSize: 4,
    bodyPadY: 4,
    bodyPadX: 1,
    bodyColor: '#333333',
    bodyBoldColor: '#1a1a1a',
    // Uniform thin grey strokes (1 / 0.5 / 0.5) so no edge reads thicker than
    // another across the 12-month grid.
    rowBorder: 0.5,
    rowBorderColor: overviewColors.border,
    colBorder: 0.5,
    colBorderColor: overviewColors.border,
    negColor: '#D94545',
    specialColor: '#8B5CF6',
    outerRadius: 8,
    outerBorder: 1,
    // Particulars column kept narrow (long labels wrap to 2-3 lines) so the 12
    // month columns get the width they need for crore values.
    flex: { particular: 8, month: 7 },
} as const;

const getData = (data: ReportData): AggBankingProps | undefined => data.aggBanking;

// ═══════════════════════════════════════════════════════════════
//                            S C R E E N
// ═══════════════════════════════════════════════════════════════

const ScreenBankTable: React.FC<{ table: AggBankingTable; showTitle?: boolean }> = ({ table, showTitle = true }) => (
    // overflowX:auto so a narrow preview column scrolls the 12-month grid
    // horizontally instead of letting cell values spill over their borders.
    // At the export's A4 width the table fits, so this is a no-op there.
    <div style={{ overflowX: 'auto' }}>
        {showTitle && table.title ? (
            <div
                style={{ fontSize: ptToPx(12), fontFamily: SF.sans, fontWeight: 700, color: overviewColors.ink, marginBottom: ptToPx(7.5) }}
            >
                {table.title}
            </div>
        ) : null}
        <div
            style={{
                border: `${tbl.outerBorder}px solid ${overviewColors.border}`,
                borderRadius: ptToPx(tbl.outerRadius),
                overflow: 'hidden',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    background: tbl.headerBg,
                    borderBottom: `${ptToPx(tbl.headerBorder)}px solid ${tbl.headerBorderColor}`,
                }}
            >
                <span
                    style={{
                        flex: tbl.flex.particular,
                        fontSize: ptToPx(tbl.headerSize),
                        fontFamily: SF.sans,
                        fontWeight: tbl.headerWeight,
                        color: tbl.headerColor,
                        letterSpacing: `${tbl.headerLetterSpacing}px`,
                        textTransform: 'uppercase',
                        padding: `${ptToPx(tbl.headerPadY)}px ${ptToPx(tbl.headerPadX)}px`,
                        minWidth: 0,
                    }}
                >
                    {table.headerLabel}
                </span>
                {table.headers.map((h) => (
                    <span
                        key={h.key}
                        style={{
                            flex: tbl.flex.month,
                            fontSize: ptToPx(tbl.headerSize),
                            fontFamily: SF.sans,
                            fontWeight: tbl.headerWeight,
                            color: tbl.headerColor,
                            letterSpacing: `${tbl.headerLetterSpacing}px`,
                            textTransform: 'uppercase',
                            textAlign: 'right',
                            padding: `${ptToPx(tbl.headerPadY)}px ${ptToPx(tbl.headerPadX)}px`,
                            minWidth: 0,
                            borderLeft: `${ptToPx(tbl.colBorder)}px solid ${tbl.colBorderColor}`,
                        }}
                    >
                        {h.label}
                    </span>
                ))}
            </div>
            {table.rows.map((row, i) => (
                <div key={i} style={{ display: 'flex', borderBottom: `${ptToPx(tbl.rowBorder)}px solid ${tbl.rowBorderColor}` }}>
                    <span
                        style={{
                            flex: tbl.flex.particular,
                            fontSize: ptToPx(tbl.bodySize),
                            fontFamily: SF.sans,
                            fontWeight: row.bold ? 700 : 400,
                            color: row.bold ? tbl.bodyBoldColor : tbl.bodyColor,
                            padding: `${ptToPx(tbl.bodyPadY)}px ${ptToPx(tbl.bodyPadX)}px`,
                            minWidth: 0,
                            overflowWrap: 'break-word',
                        }}
                    >
                        {row.particular}
                    </span>
                    {table.headers.map((h) => {
                        const cell = row.values[h.key];
                        const cellColor = cell?.special ? tbl.specialColor : cell?.negative ? tbl.negColor : tbl.bodyColor;
                        return (
                            <span
                                key={h.key}
                                style={{
                                    flex: tbl.flex.month,
                                    fontSize: ptToPx(tbl.bodyMonoSize),
                                    fontFamily: SF.mono,
                                    textAlign: 'right',
                                    color: cellColor,
                                    padding: `${ptToPx(tbl.bodyPadY)}px ${ptToPx(tbl.bodyPadX)}px`,
                                    minWidth: 0,
                                    whiteSpace: 'nowrap',
                                    borderLeft: `${ptToPx(tbl.colBorder)}px solid ${tbl.colBorderColor}`,
                                }}
                            >
                                {cell?.text ?? DASH}
                            </span>
                        );
                    })}
                </div>
            ))}
        </div>
    </div>
);

export const ScreenAggBankingBody: React.FC<{ d: AggBankingProps }> = ({ d }) => (
    <div>
        {d.stats.length > 0 ? <ScreenStatStrip stats={d.stats} /> : null}
        <ScreenBankTable table={d.mainTable} showTitle={false} />
        <div style={{ fontSize: ptToPx(7.5), fontFamily: SF.sans, color: '#bbbbbb', marginTop: ptToPx(4) }}>
            All amounts in ₹ · Negative values shown in parentheses (red)
        </div>

        {d.bounceTable ? (
            <div style={{ marginTop: ptToPx(space.sp12) }}>
                <ScreenBankTable table={d.bounceTable} />
            </div>
        ) : null}

        {d.creditsDebitsTable ? (
            <div style={{ marginTop: ptToPx(space.sp8) }}>
                <ScreenBankTable table={d.creditsDebitsTable} />
            </div>
        ) : null}

        {d.paymentMethodTable ? (
            <div style={{ marginTop: ptToPx(space.sp8) }}>
                <ScreenBankTable table={d.paymentMethodTable} />
            </div>
        ) : null}

        <div style={{ fontSize: ptToPx(7.5), fontFamily: SF.sans, color: '#bbbbbb', marginTop: ptToPx(4) }}>
            All amounts in ₹ · "–" indicates data not available
        </div>
    </div>
);

const ScreenAggBanking: React.FC<{ data: ReportData; index: string }> = ({ data, index }) => {
    const d = getData(data);
    if (!d) return null;
    return (
        <ScreenSection kicker="Banking" title="Aggregated Banking" subtitle={aggBankingSubtitle(d.accountCount)} index={index}>
            <ScreenAggBankingBody d={d} />
        </ScreenSection>
    );
};

// ════════════════════════════════════════════════════════════════
//                          M O D U L E   D E F
// ════════════════════════════════════════════════════════════════

export const aggBankingModule: ModuleDefinition = {
    meta: {
        id: 'aggBanking',
        title: 'Aggregated Banking',
        description: 'Consolidated bank statement summary, bounce transactions, credits/debits and payment methods.',
        dataSource: 'Banking Statements',
    },
    hasData: (d) => !!d.aggBanking && d.aggBanking.mainTable.rows.length > 0,
    Screen: ScreenAggBanking,
};
